import React from 'react';
import manifest from '../config/photoManifest.json';

/**
 * A competition photo, sized for the device that's asking.
 *
 * Give it a slug from src/config/photoManifest.json (generated by
 * scripts/optimize-photos.js) and it renders a <picture> that offers WebP with
 * a JPEG fallback at 640 / 1280 / 1920, and lets the browser pick.
 *
 *   <OptimizedImage slug="poeple-chatting-in-lobby-after-comp"
 *                   alt="Dancers and families in the lobby after the competition" />
 *
 * Three things here matter specifically because most visitors are on a phone:
 *
 *   1. `sizes` tells the browser how wide the photo will actually be BEFORE
 *      layout, so it can start the right download immediately. Without it the
 *      browser assumes full viewport width and pulls the largest file.
 *   2. The wrapper reserves the photo's aspect ratio from the manifest, so the
 *      page doesn't reflow as each photo lands — the difference between a page
 *      that settles and one that jumps while you're reading it.
 *   3. Everything is lazy by default. The one exception is a hero, which
 *      should pass `priority` so it loads eagerly and at high fetch priority;
 *      lazy-loading the image someone is already looking at makes the page
 *      feel slower, not faster.
 */
const OptimizedImage = ({
  slug,
  alt,
  sizes = '100vw',
  priority = false,
  className = '',
  style = {},
  imgStyle = {},
  objectPosition = 'center',
  rounded = false,
  ...rest
}) => {
  const photo = manifest[slug];

  if (!photo) {
    // Loud in development, invisible in production: a missing photo should
    // never blow up a page a family is reading, but it also shouldn't slip
    // through unnoticed while someone is building one.
    if (process.env.NODE_ENV === 'development') {
      console.error(`OptimizedImage: no photo "${slug}" in photoManifest.json. ` +
        `Run: node scripts/optimize-photos.js`);
      return (
        <div style={{ ...style, background: '#fdecea', color: '#8a2323', padding: '1rem',
                      fontSize: '0.8rem', textAlign: 'center', borderRadius: rounded ? 12 : 0 }}>
          Missing photo: {slug}
        </div>
      );
    }
    return null;
  }

  const srcSet = (ext) =>
    photo.widths.map((w) => `/photos/${slug}-${w}.${ext} ${w}w`).join(', ');

  return (
    <div
      className={className}
      style={{
        position: 'relative',
        width: '100%',
        aspectRatio: String(photo.aspectRatio),
        overflow: 'hidden',
        background: '#f0ede8',      // holds the space while the photo arrives
        borderRadius: rounded ? 12 : 0,
        ...style,
      }}
    >
      <picture>
        <source type="image/webp" srcSet={srcSet('webp')} sizes={sizes} />
        <img
          src={`/photos/${slug}-1280.jpg`}
          srcSet={srcSet('jpg')}
          sizes={sizes}
          alt={alt}
          width={photo.width}
          height={photo.height}
          loading={priority ? 'eager' : 'lazy'}
          decoding={priority ? 'sync' : 'async'}
          fetchpriority={priority ? 'high' : undefined}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            objectPosition,
            display: 'block',
            ...imgStyle,
          }}
          {...rest}
        />
      </picture>
    </div>
  );
};

export default OptimizedImage;
